/**
 * Results Normalizer - Clean data normalization without rescue logic
 * 
 * This replaces the complex chart transformer rescue system with 
 * straightforward data normalization that maintains data integrity.
 */

import { SimulationResult, ADTScenario } from '@/types/adt';

export interface NormalizedChartDataPoint {
  year: number;
  age: number;
  portfolioValue: number;
  phase: 'accumulation' | 'retirement';
  realPortfolioValue?: number;
  label: string;
}

export class ResultsNormalizer {
  
  /**
   * Normalize simulation result to ensure consistent data structure
   * CRITICAL: Preserves trialOutcomes and timelinePercentiles for charts
   */
  static normalizeSimulationResult(
    result: SimulationResult,
    scenario: ADTScenario
  ): SimulationResult {
    const normalized = { ...result };

    // PRESERVE CRITICAL CHART DATA - these must not be lost during normalization
    if (result.trialOutcomes && Array.isArray(result.trialOutcomes)) {
      normalized.trialOutcomes = [...result.trialOutcomes];
    }
    
    if (result.timelinePercentiles && Array.isArray(result.timelinePercentiles)) {
      normalized.timelinePercentiles = [...result.timelinePercentiles];
    }

    // 1. Ensure all numeric fields are valid
    normalized.successRate = this.ensureValidNumber(result.successRate, 0);
    normalized.shortfallProbability = this.ensureValidNumber(result.shortfallProbability, 100 - normalized.successRate);
    normalized.medianOutcome = this.ensureValidNumber(result.medianOutcome, 0);
    normalized.averageOutcome = this.ensureValidNumber(result.averageOutcome, normalized.medianOutcome);
    normalized.bestCaseOutcome = this.ensureValidNumber(result.bestCaseOutcome, normalized.medianOutcome * 1.5);
    normalized.worstCaseOutcome = this.ensureValidNumber(result.worstCaseOutcome, normalized.medianOutcome * 0.5);

    // 2. Normalize timeline data
    if (result.timelineData && Array.isArray(result.timelineData)) {
      normalized.timelineData = this.normalizeTimelineData(result.timelineData, scenario);
    } else {
      // If no timeline data, create minimal placeholder
      normalized.timelineData = this.createMinimalTimeline(scenario);
    }

    // 3. Normalize chart data
    if (result.chartData && Array.isArray(result.chartData)) {
      normalized.chartData = this.normalizeChartData(result.chartData, scenario);
    } else {
      // Generate chart data from timeline data
      normalized.chartData = this.generateChartDataFromTimeline(normalized.timelineData);
    }

    // 4. Normalize percentiles
    if (result.percentiles) {
      normalized.percentiles = this.normalizePercentiles(result.percentiles, normalized.medianOutcome);
    } else {
      normalized.percentiles = this.createDefaultPercentiles(normalized.medianOutcome);
    }

    // 5. Ensure scenarios array exists
    if (!result.scenarios || !Array.isArray(result.scenarios)) {
      normalized.scenarios = this.createDefaultScenarios(normalized.medianOutcome);
    }

    return normalized;
  }

  /**
   * Calculate income and expense breakdown for timeline points
   */
  private static calculateIncomeExpenseBreakdown(point: any, scenario: ADTScenario, yearIndex: number): void {
    const currentAge = scenario.basicInformation?.currentAge || 35;
    const retirementAge = scenario.basicInformation?.retirementAge || 65;
    const age = currentAge + yearIndex;
    const isRetired = age >= retirementAge;
    
    // Employment income - only during accumulation phase
    point.employmentIncome = isRetired ? 0 : (scenario.incomeExpenses?.primaryIncome || 0);
    
    // Portfolio withdrawal - only during retirement
    if (isRetired) {
      const withdrawalRate = scenario.strategy?.withdrawalRate || 4;
      point.portfolioWithdrawal = point.portfolioValue * (withdrawalRate / 100);
    } else {
      point.portfolioWithdrawal = 0;
    }
    
    // Pension income - starts at pension age (assumed 67)
    const pensionAge = 67;
    point.pensionIncome = age >= pensionAge ? (scenario.incomeExpenses?.secondaryIncome || 0) : 0;
    
    // Property rental income - use a default property income estimate
    point.propertyRentalIncome = scenario.enhancedDebtConfiguration ? 0 : 0; // Placeholder
    
    // Total expenses - basic living costs adjusted for inflation
    const totalExpenses = (scenario.incomeExpenses?.housingExpenses || 0) +
                          (scenario.incomeExpenses?.utilitiesExpenses || 0) +
                          (scenario.incomeExpenses?.foodExpenses || 0) +
                          (scenario.incomeExpenses?.transportExpenses || 0) +
                          (scenario.incomeExpenses?.entertainmentExpenses || 0) +
                          (scenario.incomeExpenses?.healthcareExpenses || 0) +
                          (scenario.incomeExpenses?.insuranceExpenses || 0) +
                          (scenario.incomeExpenses?.otherExpenses || 0);
    
    const baseExpenses = totalExpenses > 0 ? totalExpenses : 
                        (scenario.incomeExpenses?.primaryIncome || 0) * 0.7; // 70% of income if not specified
    const inflationRate = scenario.capitalMarketsAssumptions?.inflationRate || 2.5;
    const inflationFactor = Math.pow(1 + inflationRate / 100, yearIndex);
    point.totalExpenses = baseExpenses * inflationFactor;
    
    // Calculate total income
    point.totalIncome = point.employmentIncome + point.portfolioWithdrawal + 
                       point.pensionIncome + point.propertyRentalIncome;
  }

  /**
   * Ensure a value is a valid finite number, provide fallback if not
   */
  private static ensureValidNumber(value: any, fallback: number): number {
    if (typeof value === 'number' && isFinite(value) && !isNaN(value)) {
      return value;
    }
    return fallback;
  }

  /**
   * Normalize timeline data to ensure consistent structure
   */
  private static normalizeTimelineData(timelineData: any[], scenario: ADTScenario): any[] {
    const currentAge = scenario.basicInformation?.currentAge || 35;
    const retirementAge = scenario.basicInformation?.retirementAge || 65;
    
    return timelineData.map((point, index) => {
      const normalizedPoint = { ...point };
      
      // Ensure consistent age calculation
      normalizedPoint.age = this.ensureValidNumber(point.age, currentAge + index);
      
      // Ensure year is consistent with age
      normalizedPoint.year = this.ensureValidNumber(point.year, index + 1);
      
      // Ensure portfolio value is valid
      normalizedPoint.portfolioValue = this.ensureValidNumber(point.portfolioValue || point.value, 0);
      normalizedPoint.value = normalizedPoint.portfolioValue; // Compatibility
      
      // Determine phase
      normalizedPoint.phase = normalizedPoint.age < retirementAge ? 'accumulation' : 'retirement';
      
      // Ensure currentAge field for compatibility
      normalizedPoint.currentAge = normalizedPoint.age;
      
      // Add real portfolio value if missing
      if (!normalizedPoint.realPortfolioValue) {
        const inflationRate = scenario.capitalMarketsAssumptions?.inflationRate || 2.5;
        const inflationFactor = Math.pow(1 + inflationRate / 100, index);
        normalizedPoint.realPortfolioValue = normalizedPoint.portfolioValue / inflationFactor;
      }
      
      // Calculate income and expense breakdown
      this.calculateIncomeExpenseBreakdown(normalizedPoint, scenario, index);
      
      return normalizedPoint;
    }).filter(point => point.age <= (scenario.lifeExpectancyAssumptions?.expectedEndOfLife || 95));
  }

  /**
   * Normalize chart data to ensure consistent structure
   */
  private static normalizeChartData(chartData: any[], scenario: ADTScenario): NormalizedChartDataPoint[] {
    const currentAge = scenario.basicInformation?.currentAge || 35;
    const retirementAge = scenario.basicInformation?.retirementAge || 65;
    
    return chartData.map((point, index) => ({
      year: this.ensureValidNumber(point.year, index + 1),
      age: this.ensureValidNumber(point.age, currentAge + index),
      portfolioValue: this.ensureValidNumber(point.portfolioValue || point.value, 0),
      phase: (point.age || currentAge + index) < retirementAge ? 'accumulation' : 'retirement',
      realPortfolioValue: this.ensureValidNumber(point.realPortfolioValue, point.portfolioValue || 0),
      label: `Age ${point.age || currentAge + index}`
    }));
  }

  /**
   * Normalize percentiles to ensure ascending order
   */
  private static normalizePercentiles(percentiles: any, medianFallback: number) {
    const p50 = this.ensureValidNumber(percentiles.p50 || percentiles.median, medianFallback);
    
    return {
      year: this.ensureValidNumber(percentiles.year, new Date().getFullYear()),
      p10: this.ensureValidNumber(percentiles.p10, p50 * 0.6),
      p25: this.ensureValidNumber(percentiles.p25, p50 * 0.8),
      p50,
      p75: this.ensureValidNumber(percentiles.p75, p50 * 1.2),
      p90: this.ensureValidNumber(percentiles.p90, p50 * 1.5)
    };
  }

  /**
   * Create minimal timeline when none exists
   */
  private static createMinimalTimeline(scenario: ADTScenario): any[] {
    const currentAge = scenario.basicInformation?.currentAge || 35;
    const retirementAge = scenario.basicInformation?.retirementAge || 65;
    const lifeExpectancy = scenario.lifeExpectancyAssumptions?.expectedEndOfLife || 90;
    
    const timeline = [];
    const totalYears = lifeExpectancy - currentAge;
    const initialAssets = (scenario.assets?.superBalance || 0) + 
                         (scenario.assets?.liquidInvestments || 0) + 
                         (scenario.assets?.cashAtBank || 0);
    
    for (let i = 0; i < totalYears; i++) {
      const age = currentAge + i;
      const phase = age < retirementAge ? 'accumulation' : 'retirement';
      
      // Simple growth model for minimal timeline
      let portfolioValue;
      if (phase === 'accumulation') {
        portfolioValue = initialAssets * Math.pow(1.07, i) + (i * 20000); // 7% growth + $20k annual contribution
      } else {
        const retirementStart = initialAssets * Math.pow(1.07, retirementAge - currentAge) + ((retirementAge - currentAge) * 20000);
        const yearsInRetirement = age - retirementAge;
        portfolioValue = Math.max(0, retirementStart * Math.pow(1.04, yearsInRetirement) - (yearsInRetirement * 60000)); // 4% growth - $60k withdrawal
      }
      
      timeline.push({
        year: i + 1,
        age,
        portfolioValue,
        value: portfolioValue,
        currentAge: age,
        phase,
        realPortfolioValue: portfolioValue / Math.pow(1.025, i)
      });
    }
    
    return timeline;
  }

  /**
   * Generate chart data from timeline data
   */
  private static generateChartDataFromTimeline(timelineData: any[]): NormalizedChartDataPoint[] {
    return timelineData.map(point => ({
      year: point.year,
      age: point.age,
      portfolioValue: point.portfolioValue || point.value || 0,
      phase: point.phase,
      realPortfolioValue: point.realPortfolioValue,
      label: `Age ${point.age}`
    }));
  }

  /**
   * Create default percentiles
   */
  private static createDefaultPercentiles(medianOutcome: number) {
    return {
      year: new Date().getFullYear() + 30,
      p10: medianOutcome * 0.6,
      p25: medianOutcome * 0.8,
      p50: medianOutcome,
      p75: medianOutcome * 1.2,
      p90: medianOutcome * 1.5
    };
  }

  /**
   * Create default scenarios
   */
  private static createDefaultScenarios(medianOutcome: number) {
    return [
      { name: 'Conservative', finalWealth: medianOutcome * 0.8, probability: 25 },
      { name: 'Expected', finalWealth: medianOutcome, probability: 50 },
      { name: 'Optimistic', finalWealth: medianOutcome * 1.3, probability: 25 }
    ];
  }

  /**
   * Validate normalized result
   */
  static validateNormalizedResult(result: SimulationResult): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!isFinite(result.successRate) || result.successRate < 0 || result.successRate > 100) {
      errors.push('Invalid success rate');
    }
    
    if (!isFinite(result.medianOutcome)) {
      errors.push('Invalid median outcome');
    }
    
    if (!result.timelineData || result.timelineData.length === 0) {
      errors.push('No timeline data');
    }
    
    if (!result.chartData || result.chartData.length === 0) {
      errors.push('No chart data');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}